// How the ad accounts are doing (BL-189; was AdsViews' AdsHealth, Seth's A3 (b), 4 Nov): adAccounts.status
// as RowList rows, in words, with no drawing of its own. Each problem is a row first, its words red (Notice
// is never red); then each ad account and what went to Google; Google's latest refusals as their own rows.
import type { LabelKind } from '../components/Label/Label.tsx';
import type { Row, RowTone } from '../components/RowList/RowList.tsx';
import { accountState, CONVERSION_WORDS, PLATFORM_WORDS } from '../components/ads-words.ts';
import { clock, dayOf, plural } from '../format.ts';
import type { AdsStatusProps } from '../schemas.ts';

/** A Label's kind as a row's state word: now is ink. */
const toneOf = (k: LabelKind): RowTone => (k === 'now' ? 'ink' : k);

/** What a problem is about, from its key: an ad account's spend (pull:<platform>), or what goes to Google. */
const problemName = (key: string): string => {
  const platform = key.startsWith('pull:') ? key.slice('pull:'.length) : null;
  return platform && platform in PLATFORM_WORDS
    ? PLATFORM_WORDS[platform as keyof typeof PLATFORM_WORDS]
    : 'Conversions to Google';
};

/**
 * adAccounts.status: each problem first, its words in red under what it's about; then each ad account,
 * its last pull (and the error, red once it's failing) and its state; then what went to Google this week.
 */
export function adsHealthRows(p: AdsStatusProps): Row[] {
  const c = p.conversions;
  const w = c.week;
  const problems: Row[] = p.notices.map((n) => ({
    id: n.key,
    name: problemName(n.key),
    lines: [{ word: n.text, tone: 'bad' }],
  }));
  const accounts: Row[] = p.accounts.map((a) => {
    const [word, kind] = accountState(a);
    const pulled = !a.connected
      ? 'Its spend is entered by hand.'
      : [
          a.customerId ? `Account ${a.customerId}` : null,
          a.lastOkAt
            ? `spend in to ${a.lastOkDay ?? dayOf(a.lastOkAt, p.timeZone)}, pulled ${dayOf(a.lastOkAt, p.timeZone)} ${clock(a.lastOkAt, p.timeZone)}`
            : 'no spend pulled yet',
        ]
          .filter(Boolean)
          .join(', ');
    return {
      id: a.platform,
      name: PLATFORM_WORDS[a.platform],
      lines: [
        { text: pulled },
        ...(a.connected && a.lastError
          ? [a.failing ? { word: a.lastError, tone: 'bad' as const } : { text: a.lastError }]
          : []),
      ],
      end: { word, tone: toneOf(kind) },
    };
  });
  const sent = (
    (c.on
      ? `On from ${c.from ?? 'today'}. The last seven days: ${w.sent} sent, ${w.queued} waiting, ${w.refused} refused, ${w.skipped} kept back`
      : 'Off: nothing is sent to Google') +
    (c.on && c.lastSentAt
      ? `. Last sent ${dayOf(c.lastSentAt, p.timeZone)}, ${clock(c.lastSentAt, p.timeZone)}`
      : '') +
    (c.adjustmentsWaiting ? `. ${plural(c.adjustmentsWaiting, 'change')} of value to send` : '')
  ).replace('.. ', '. ');
  return [
    ...problems,
    ...accounts,
    {
      id: 'conversions',
      name: 'Conversions to Google',
      lines: [{ text: sent }],
      end: {
        word: c.on ? 'On' : 'Off',
        tone: c.on && (c.shared.length || c.waitingOverADay) ? 'bad' : 'muted',
      },
    },
  ];
}

/** Google's latest refusals, one a row: the kind and the day, and Google's words in red. */
export const adsRefusalRows = (p: AdsStatusProps): Row[] =>
  p.conversions.refusals.map((r, i) => ({
    id: `${r.at}-${i}`,
    name: `${CONVERSION_WORDS[r.kind]}, ${dayOf(r.at, p.timeZone)}`,
    lines: [{ word: r.error, tone: 'bad' }],
  }));
